import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import { articles, categories } from '@/mocks/articles';
import { siteConfig } from '@/mocks/site';
import type { BreadcrumbItem } from '@/components/base/Breadcrumb';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-900 mb-4">文章未找到</h1>
            <Link to="/category/tutorial" className="text-primary-600 hover:text-primary-700 font-medium text-sm whitespace-nowrap">
              返回教程列表
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleCategory = categories.find((c) => c.slug === article.category);

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: '首页', href: '/' },
    { label: articleCategory?.name || article.category, href: `/category/${article.category}` },
    { label: article.title },
  ];

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const articleImageMap: Record<string, string> = {
    'getting-started': 'https://readdy.ai/api/search-image?query=Modern%20workspace%20with%20laptop%20showing%20a%20clean%20navigation%20page%20interface%2C%20soft%20natural%20lighting%2C%20minimalist%20desk%20setup%2C%20warm%20wooden%20textures%2C%20professional%20editorial%20photography&width=1200&height=500&seq=article-getting-started-img&orientation=landscape',
    'custom-domain': 'https://readdy.ai/api/search-image?query=Web%20domain%20configuration%20concept%20with%20clean%20minimalist%20interface%20showing%20DNS%20settings%2C%20professional%20tech%20aesthetic%2C%20soft%20lighting%2C%20modern%20UI%20design&width=1200&height=500&seq=article-domain-img&orientation=landscape',
    'team-collaboration': 'https://readdy.ai/api/search-image?query=Modern%20team%20collaboration%20workspace%20with%20multiple%20screens%20showing%20shared%20dashboard%2C%20warm%20office%20lighting%2C%20professional%20setting&width=1200&height=500&seq=article-team-img&orientation=landscape',
    'v2-0-release': 'https://readdy.ai/api/search-image?query=Modern%20software%20update%20announcement%20visual%20with%20clean%20interface%20mockup%2C%20gradient%20background%2C%20tech%20aesthetic%2C%20professional%20design&width=1200&height=500&seq=article-v2-img&orientation=landscape',
    'privacy': 'https://readdy.ai/api/search-image?query=Clean%20minimalist%20legal%20document%20concept%20with%20soft%20lighting%2C%20professional%20setting%2C%20warm%20neutral%20tones&width=1200&height=500&seq=article-privacy-img&orientation=landscape',
    'seo-optimization': 'https://readdy.ai/api/search-image?query=SEO%20optimization%20concept%20with%20clean%20search%20engine%20analytics%20dashboard%2C%20professional%20tech%20workspace%2C%20warm%20lighting%2C%20modern%20minimalist%20design&width=1200&height=500&seq=article-seo-img&orientation=landscape',
  };

  return (
    <>
      <ScrollToTop />
      <SchemaOrg
        type="Article"
        data={{
          headline: article.title,
          description: article.excerpt,
          datePublished: article.date,
          author: { '@type': 'Person', name: article.author },
          publisher: { '@type': 'Organization', name: siteConfig.siteName },
          url: `${siteConfig.siteUrl}/article/${article.slug}`,
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: breadcrumbItems.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.label,
            item: item.href ? `${siteConfig.siteUrl}${item.href}` : undefined,
          })),
        }}
      />

      <Navbar />

      <main className="pt-20">
        <article className="bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
            <Breadcrumb items={breadcrumbItems} />

            <header className="mb-8">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
                <span>{article.author}</span>
                <span>{article.date}</span>
                <span>{article.readTime}阅读</span>
              </div>
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                {article.tags.map((tag) => (
                  <span key={tag} className="text-xs text-secondary-600 bg-secondary-100 px-2.5 py-1 rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
            </header>

            {articleImageMap[article.slug] && (
              <img
                src={articleImageMap[article.slug]}
                alt={article.title}
                className="w-full h-56 md:h-80 object-cover rounded-xl mb-8"
              />
            )}

            <div
              className="prose prose-base max-w-none text-foreground-800 leading-relaxed
                [&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:font-bold [&_h2]:text-foreground-950 [&_h2]:mt-10 [&_h2]:mb-4
                [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-foreground-900 [&_h3]:mt-8 [&_h3]:mb-3
                [&_p]:text-sm [&_p]:md:text-base [&_p]:leading-relaxed [&_p]:mb-4
                [&_strong]:text-foreground-900 [&_strong]:font-semibold
                [&_br]:block [&_br]:mb-1"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="border-t border-background-200/70 mt-12 pt-8">
              <h3 className="text-lg font-semibold text-foreground-900 mb-5">相关文章</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.slug}
                    to={`/article/${ra.slug}`}
                    className="block p-4 rounded-lg bg-background-100 hover:bg-background-200/70 transition-colors"
                  >
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1.5 line-clamp-2">
                      {ra.title}
                    </h4>
                    <p className="text-xs text-foreground-500">{ra.date} &middot; {ra.readTime}阅读</p>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}